/**
 * A grid of media thumbnails that opens a full-screen lightbox carousel on tap —
 * swipe, arrow keys, and prev/next buttons, with native video playback. Powered
 * by yet-another-react-lightbox. Shared by the activity feed and the project /
 * task activity views so media looks and behaves the same everywhere.
 */
import { useState } from 'react';
import { Play } from 'lucide-react';
import Lightbox from 'yet-another-react-lightbox';
import Video from 'yet-another-react-lightbox/plugins/video';
import 'yet-another-react-lightbox/styles.css';
import type { ActivityMedia } from '@vari-crew/schemas';
import { attachmentFileUrl } from '@/api/attachments';
import { cn } from '@/lib/cn';

export function MediaGallery({
  media,
  size = 'md',
}: {
  media: ActivityMedia[];
  size?: 'sm' | 'md';
}) {
  const [index, setIndex] = useState(-1);
  if (media.length === 0) return null;

  const box = size === 'sm' ? 'w-16 h-16' : 'w-20 h-20';
  const slides = media.map((m) =>
    m.kind === 'video'
      ? { type: 'video' as const, sources: [{ src: attachmentFileUrl(m), type: m.mime_type }] }
      : { src: attachmentFileUrl(m) },
  );

  return (
    <>
      <div className="flex flex-wrap gap-2 mt-2">
        {media.map((m, i) => (
          <button
            key={m.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`Open ${m.kind === 'video' ? 'video' : 'photo'} ${i + 1}`}
            className={cn(
              box,
              'relative rounded-lg border border-line overflow-hidden bg-fill-soft hover:ring-2 hover:ring-accent/40 transition',
            )}
          >
            {m.kind === 'video' ? (
              <>
                <video
                  src={attachmentFileUrl(m)}
                  className="w-full h-full object-cover"
                  preload="metadata"
                  muted
                  playsInline
                />
                <span className="absolute inset-0 grid place-items-center bg-black/15">
                  <Play className="w-5 h-5 text-white drop-shadow" fill="currentColor" />
                </span>
              </>
            ) : (
              <img
                src={attachmentFileUrl(m)}
                alt=""
                loading="lazy"
                className="w-full h-full object-cover"
              />
            )}
          </button>
        ))}
      </div>

      <Lightbox
        open={index >= 0}
        index={Math.max(0, index)}
        close={() => setIndex(-1)}
        slides={slides}
        plugins={[Video]}
        controller={{ closeOnBackdropClick: true }}
        styles={{ container: { backgroundColor: 'rgba(0,0,0,0.92)' } }}
        video={{ controls: true, playsInline: true }}
      />
    </>
  );
}
